import { ArrowUpRight, SectionBadge } from "./SitePrimitives";

type Post = { category: string; title: string; excerpt: string; href: string; date: string; image?: string };

function external(href: string) {
  return /^https?:\/\//.test(href) ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

export function SiteBlog({
  badge,
  title,
  ctaLabel,
  ctaHref,
  author,
  items,
}: {
  badge: string;
  title: string;
  ctaLabel: string;
  ctaHref: string;
  author?: string;
  items: Post[];
}) {
  const [featured, ...rest] = items;
  return (
    <section id="blog" className="pt-[108px] min-[810px]:pt-32 min-[1200px]:pt-[120px]">
      <div className="site-container">
        <div className="flex flex-col gap-4 min-[810px]:flex-row min-[810px]:items-end min-[810px]:justify-between">
          <div className="flex flex-col gap-2 min-[810px]:gap-[11px]">
            <SectionBadge>{badge}</SectionBadge>
            {title ? <h2 className="site-h2 text-white">{title}</h2> : null}
          </div>
          {ctaLabel && items.length ? (
            <a
              href={ctaHref}
              {...external(ctaHref)}
              className="inline-flex items-center gap-2 pb-[6px] text-[16px] font-bold leading-[1.45] text-[#9F9F9F] transition-colors hover:text-white"
            >
              {ctaLabel}
              <ArrowUpRight />
            </a>
          ) : null}
        </div>
        {featured ? (
          <div className="mt-[39px] grid gap-[52px] min-[810px]:mt-12 min-[810px]:gap-10 min-[810px]:grid-cols-[452px_1fr] min-[1200px]:mt-[63px] min-[1200px]:grid-cols-[648px_1fr]">
            <a href={featured.href} {...external(featured.href)} className="group block">
              {featured.image ? (
                <img
                  src={featured.image}
                  alt=""
                  className="h-[214px] w-full rounded-2xl object-cover min-[810px]:h-[250px] min-[1200px]:h-[271px]"
                  loading="lazy"
                />
              ) : null}
              <div className="relative px-3 pt-[17px]">
                <span className="absolute right-3 top-[17px] text-[#627072] transition-colors group-hover:text-white">
                  <ArrowUpRight className="h-[10px] w-[10px]" />
                </span>
                {featured.category ? (
                  <p className="font-['Inter'] text-[14px] font-semibold uppercase leading-[20px] text-[#1D9793]">
                    {featured.category}
                  </p>
                ) : null}
                <h3 className="site-h2 mt-3 max-w-[594px] pr-6 text-white">{featured.title}</h3>
                {featured.excerpt ? <p className="site-lead mt-3">{featured.excerpt}</p> : null}
                <p className="site-body mt-3 flex items-center gap-3 text-[#A3A3A3] min-[1200px]:text-[15px]">
                  {author ? <span className="text-white">{author}</span> : null}
                  {author && featured.date ? <span className="h-[5px] w-[5px] rounded-full bg-[#A3A3A3]" /> : null}
                  {featured.date ? <span>{featured.date}</span> : null}
                </p>
              </div>
            </a>
            {rest.length ? (
              <div className="flex flex-col gap-[37px]">
                {rest.map((post) => (
                  <a key={post.href + post.title} href={post.href} {...external(post.href)} className="flex gap-6 min-[810px]:h-40">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt=""
                        className="h-40 w-[153px] shrink-0 rounded-2xl object-cover min-[1200px]:w-[238px]"
                        loading="lazy"
                      />
                    ) : null}
                    <div className="flex min-w-0 flex-col gap-[10px] min-[1200px]:pr-3">
                      {post.category ? (
                        <p className="font-['Inter'] text-[12px] font-semibold uppercase leading-[12px] text-[#1D9793]">
                          {post.category}
                        </p>
                      ) : null}
                      <h3 className="line-clamp-2 font-['Inter'] text-[16px] font-bold leading-[1.3] tracking-[-0.01em] text-white min-[1200px]:text-[20px]">
                        {post.title}
                      </h3>
                      {post.excerpt ? <p className="site-body line-clamp-2 text-[#9F9F9F]">{post.excerpt}</p> : null}
                      {post.date ? (
                        <p className="site-body text-[#A3A3A3] min-[1200px]:text-[15px]">{post.date}</p>
                      ) : null}
                    </div>
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
